/**
 * The relay's HTTP API from the browser, plus where the browser keeps its
 * credentials.
 *
 * The control plane is single-owner, so starting a session needs the owner key.
 * It is held in localStorage after being entered once rather than embedded in
 * the bundle, which would hand it to every viewer who opens a share link.
 */
export interface StartedSession {
  sessionId: string;
  agentToken: string;
  viewerToken: string;
  joinCode: string;
  joinCodeExpiresAt: number;
  viewerUrl: string;
  controllerUrl: string;
}

const OWNER_KEY_STORAGE = 'lt.ownerApiKey';

export function storedOwnerKey(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY_STORAGE);
  } catch {
    return null; // private mode, or storage disabled
  }
}

export function storeOwnerKey(key: string): void {
  try {
    localStorage.setItem(OWNER_KEY_STORAGE, key.trim());
  } catch {
    // Nothing to do: the key just has to be re-entered next time.
  }
}

export function clearOwnerKey(): void {
  try {
    localStorage.removeItem(OWNER_KEY_STORAGE);
  } catch {
    // ignore
  }
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    message: string,
    readonly status: number,
    readonly reason?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const { token, ...rest } = init;
  const response = await fetch(path, {
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...rest.headers,
    },
  });
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new ApiError(body?.error ?? `request failed with ${response.status}`, response.status, body?.reason);
  }
  return body as T;
}

export function startSession(ownerKey: string): Promise<StartedSession> {
  return request('/api/sessions', {
    method: 'POST',
    body: JSON.stringify({ language: 'en' }),
    token: ownerKey,
  });
}

/** Exchanges the short code the QR carried for a controller token. */
export function joinAsController(sessionId: string, code: string): Promise<{ controllerToken: string }> {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/join`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export function refreshPairingCode(
  sessionId: string,
  ownerKey: string,
): Promise<{ joinCode: string; expiresAt: number; controllerUrl: string }> {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/pairing-code`, {
    method: 'POST',
    body: '{}',
    token: ownerKey,
  });
}

export interface PastSession {
  sessionId: string;
  status: string;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
}

/** Recent meetings, newest first. Owner only. */
export async function listSessions(ownerKey: string): Promise<PastSession[]> {
  const body = await request<{ sessions: PastSession[] }>('/api/sessions', { token: ownerKey });
  return body.sessions;
}

/** A viewer link for a past meeting, minted with the owner key. */
export function mintViewerToken(sessionId: string, ownerKey: string): Promise<{ viewerToken: string }> {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/viewer-token`, {
    method: 'POST',
    body: '{}',
    token: ownerKey,
  });
}

export function endSession(sessionId: string, token: string): Promise<unknown> {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/end`, {
    method: 'POST',
    body: JSON.stringify({ reason: 'user' }),
    token,
  });
}

/** Where to reach the relay. Same origin in prod; the dev server proxies. */
export function relayUrls(): { httpBase: string; wsUrl: string } {
  const { protocol, host } = window.location;
  return {
    httpBase: '',
    wsUrl: `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}/ws`,
  };
}
